import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, ChevronLeft, ChevronRight, Users, Clock, X, MapPin, User,
  CalendarDays, CalendarRange, Ban, Loader2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  format, startOfWeek, addDays, addWeeks, subWeeks, isSameDay, parseISO, isValid,
} from 'date-fns'
import { classes, staff } from '../api/endpoints.js'
import ClassDrawer from '../components/ClassDrawer.jsx'

/* ---------------------------------------------------------------------------
   Calendar geometry. Gym hours come from the backend .env (5am–11pm).
   Layout approach follows the SimPLex scheduler: fixed time gutter, hour lines
   stronger than half-hour lines, events absolutely positioned, collision lanes,
   and a now-line drawn only on today's column.
--------------------------------------------------------------------------- */
const START_HOUR = 5
const END_HOUR = 23
const PX_PER_HOUR = 62
const GUTTER = 56
const TOTAL_H = (END_HOUR - START_HOUR) * PX_PER_HOUR

const CATEGORY_COLORS = {
  group_fitness: '#097EFD',
  semi_private: '#7C5CFF',
  bootcamp: '#F0554A',
  recovery: '#2DD4BF',
  open_gym: '#8A93A0',
  specialty: '#E256C8',
  event: '#F0BA4A',
}

const CATEGORY_LABELS = {
  group_fitness: 'Group fitness',
  semi_private: 'Semi-private',
  bootcamp: 'Bootcamp',
  recovery: 'Recovery',
  open_gym: 'Open gym',
  specialty: 'Specialty',
  event: 'Event',
}
const FALLBACK = '#097EFD'

const hex = (c) => (c && /^#?[0-9a-f]{6}$/i.test(c) ? (c[0] === '#' ? c : '#' + c) : null)

/** WCAG relative luminance -> pick ink that actually reads on the fill. */
function inkFor(bg) {
  const h = bg.replace('#', '')
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255)
  const f = (v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4)
  const L = 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b)
  return L > 0.45 ? '#10141A' : '#FFFFFF'
}

const colorFor = (c) =>
  hex(c.color) || CATEGORY_COLORS[String(c.category ?? '').toLowerCase()] || FALLBACK

const toDate = (v) => {
  if (!v) return null
  const d = typeof v === 'string' ? parseISO(v) : new Date(v)
  return isValid(d) ? d : null
}
const minsFromMidnight = (d) => d.getHours() * 60 + d.getMinutes()

/** Assign side-by-side lanes to overlapping events. */
function layout(events) {
  const sorted = [...events].sort((a, b) => a.startM - b.startM || b.endM - a.endM)
  const lanes = []
  sorted.forEach((e) => {
    let i = 0
    while (i < lanes.length && lanes[i] > e.startM) i++
    if (i === lanes.length) lanes.push(e.endM)
    else lanes[i] = e.endM
    e.lane = i
  })
  // cluster width: events that overlap share the widest lane count
  sorted.forEach((e) => {
    const overlapping = sorted.filter((o) => o.startM < e.endM && o.endM > e.startM)
    e.lanes = Math.max(...overlapping.map((o) => o.lane)) + 1
  })
  return sorted
}

/* ------------------------------------------------------------------ event -- */
function EventCard({ ev, onClick }) {
  const bg = colorFor(ev.raw)
  const ink = inkFor(bg)
  const cancelled = ev.raw.is_cancelled
  const full = ev.raw.is_full || (ev.raw.capacity && ev.raw.confirmed_count >= ev.raw.capacity)
  const w = 100 / ev.lanes
  return (
    <button
      type="button"
      onClick={() => onClick(ev.raw)}
      title={`${ev.raw.name} · ${format(ev.start, 'h:mm a')}–${format(ev.end, 'h:mm a')}`}
      className="absolute rounded-md px-2 py-1 text-left overflow-hidden group
                 transition-[transform,box-shadow,filter] duration-150 ease-snap
                 hover:z-20 hover:scale-[1.015] focus-visible:z-20"
      style={{
        top: ev.top, height: Math.max(ev.height, 20),
        left: `calc(${ev.lane * w}% + 2px)`,
        width: `calc(${w}% - 4px)`,
        background: cancelled ? 'transparent' : bg,
        border: `1px solid ${cancelled ? 'rgb(var(--border-strong))' : bg}`,
        color: cancelled ? 'rgb(var(--text-dim))' : ink,
        filter: cancelled ? 'grayscale(1)' : undefined,
        boxShadow: cancelled ? 'none' : '0 1px 2px rgb(0 0 0 / .25)',
      }}
    >
      <div className="flex items-start gap-1">
        <span
          className={`font-semibold text-[12.5px] leading-tight truncate flex-1 ${cancelled ? 'line-through' : ''}`}
        >
          {ev.raw.name}
        </span>
        {full && !cancelled && (
          <span className="text-[9px] font-bold uppercase px-1 rounded-sm shrink-0"
                style={{ background: ink, color: bg }}>
            Full
          </span>
        )}
      </div>
      {ev.height >= 38 && (
        <div className="text-[11px] leading-tight opacity-90 truncate">
          {format(ev.start, 'h:mm')}–{format(ev.end, 'h:mm a')}
          {ev.raw.coach_name ? ` · ${ev.raw.coach_name}` : ''}
        </div>
      )}
      {ev.height >= 56 && (
        <div className="text-[11px] leading-tight opacity-85 truncate">
          {ev.raw.capacity != null
            ? `${ev.raw.confirmed_count ?? 0}/${ev.raw.capacity}`
            : `${ev.raw.confirmed_count ?? 0} booked`}
          {ev.raw.waitlist_count ? ` · ${ev.raw.waitlist_count} waiting` : ''}
          {ev.raw.location ? ` · ${ev.raw.location}` : ''}
        </div>
      )}
    </button>
  )
}

/* ------------------------------------------------------------------ modal -- */
function ClassModal({ open, onClose, initialDate }) {
  const qc = useQueryClient()
  const [f, setF] = useState({
    name: '', category: 'group_fitness', coach_id: '', location: '',
    date: initialDate ? format(initialDate, 'yyyy-MM-dd') : '',
    time: '06:00', duration_minutes: 60, capacity: 20, description: '',
  })
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }))

  const { data: staffData } = useQuery({
    queryKey: ['staff', 'picker'],
    queryFn: () => staff.list().then((r) => r.data),
    enabled: open,
  })
  const coaches = Array.isArray(staffData) ? staffData : staffData?.items ?? []

  const create = useMutation({
    // the API takes start_time + end_time — the old form posted starts_at and
    // duration_minutes, which silently failed.
    mutationFn: () => {
      const start = new Date(`${f.date}T${f.time}`)
      const end = new Date(start.getTime() + Number(f.duration_minutes) * 60000)
      return classes.create({
        name: f.name,
        description: f.description || null,
        category: f.category,
        coach_id: f.coach_id || null,
        location: f.location || null,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        duration_minutes: Number(f.duration_minutes),
        capacity: f.capacity ? Number(f.capacity) : null,
        waitlist_enabled: true,
        is_published: true,
      })
    },
    onSuccess: () => {
      toast.success('Class scheduled')
      qc.invalidateQueries({ queryKey: ['classes'] })
      onClose()
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not create class'),
  })

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm animate-fade-in p-4"
         onClick={onClose}>
      <div className="card w-full max-w-lg animate-pop max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-bold">Schedule a class</h3>
          <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="label" htmlFor="c-name">Class name</label>
            <input id="c-name" className="input-field" value={f.name} onChange={set('name')}
                   placeholder="Metcon, Open Gym, Barbell Club…" />
          </div>
          <div>
            <label className="label" htmlFor="c-cat">Category</label>
            <select id="c-cat" className="input-field" value={f.category} onChange={set('category')}>
              {Object.keys(CATEGORY_COLORS).map((k) => (
                <option key={k} value={k}>{CATEGORY_LABELS[k]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="c-coach">Coach</label>
            <select id="c-coach" className="input-field" value={f.coach_id} onChange={set('coach_id')}>
              <option value="">Unassigned</option>
              {coaches.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.full_name || `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() || s.email}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="c-date">Date</label>
            <input id="c-date" type="date" className="input-field" value={f.date} onChange={set('date')} />
          </div>
          <div>
            <label className="label" htmlFor="c-time">Start</label>
            <input id="c-time" type="time" className="input-field" value={f.time} onChange={set('time')} />
          </div>
          <div>
            <label className="label" htmlFor="c-dur">Duration (min)</label>
            <input id="c-dur" type="number" min="15" step="15" className="input-field"
                   value={f.duration_minutes} onChange={set('duration_minutes')} />
          </div>
          <div>
            <label className="label" htmlFor="c-cap">Capacity</label>
            <input id="c-cap" type="number" min="1" className="input-field"
                   value={f.capacity} onChange={set('capacity')} />
          </div>
          <div className="col-span-2">
            <label className="label" htmlFor="c-loc">Location</label>
            <input id="c-loc" className="input-field" value={f.location} onChange={set('location')}
                   placeholder="Main floor, Turf, Studio…" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={create.isPending || !f.name || !f.date}
                  onClick={() => create.mutate()}>
            {create.isPending ? 'Scheduling…' : 'Schedule class'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------- page -- */
export default function SchedulePage() {
  const [anchor, setAnchor] = useState(new Date())
  const [view, setView] = useState('week')
  const [modal, setModal] = useState(false)
  const [detail, setDetail] = useState(null)
  const [now, setNow] = useState(new Date())
  const bodyRef = useRef(null)

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(t)
  }, [])

  // scroll to a sensible hour on mount (06:00), not midnight
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = (6 - START_HOUR) * PX_PER_HOUR - 8
  }, [])

  const days = useMemo(() => {
    if (view === 'day') return [anchor]
    const s = startOfWeek(anchor, { weekStartsOn: 1 })
    return Array.from({ length: 7 }, (_, i) => addDays(s, i))
  }, [anchor, view])

  const rangeFrom = days[0]
  const rangeTo = days[days.length - 1]

  const { data, isLoading } = useQuery({
    queryKey: ['classes', format(rangeFrom, 'yyyy-MM-dd'), format(rangeTo, 'yyyy-MM-dd')],
    queryFn: () => classes.list({
      from: format(rangeFrom, 'yyyy-MM-dd'),
      to: format(rangeTo, 'yyyy-MM-dd'),
    }).then((r) => r.data),
  })
  const list = Array.isArray(data) ? data : data?.items ?? []

  const byDay = useMemo(() => {
    const map = days.map(() => [])
    list.forEach((c) => {
      const start = toDate(c.start_time)
      if (!start) return
      const end = toDate(c.end_time) ?? new Date(start.getTime() + (c.duration_minutes ?? 60) * 60000)
      const idx = days.findIndex((d) => isSameDay(d, start))
      if (idx === -1) return
      const startM = minsFromMidnight(start)
      const endM = Math.max(minsFromMidnight(end), startM + 15)
      map[idx].push({
        raw: c, start, end, startM, endM,
        top: ((startM - START_HOUR * 60) / 60) * PX_PER_HOUR,
        height: ((endM - startM) / 60) * PX_PER_HOUR - 2,
      })
    })
    return map.map(layout)
  }, [list, days])

  const hours = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i)
  const todayIdx = days.findIndex((d) => isSameDay(d, now))
  const nowTop = ((minsFromMidnight(now) - START_HOUR * 60) / 60) * PX_PER_HOUR
  const nowVisible = todayIdx !== -1 && nowTop >= 0 && nowTop <= TOTAL_H

  const shift = (dir) => setAnchor((a) =>
    view === 'day' ? addDays(a, dir) : (dir > 0 ? addWeeks(a, 1) : subWeeks(a, 1)))

  const title = view === 'day'
    ? format(anchor, 'EEEE d MMMM yyyy')
    : `${format(rangeFrom, 'd MMM')} – ${format(rangeTo, 'd MMM yyyy')}`

  return (
    <div className="space-y-4">
      {/* toolbar */}
      <div className="page-header !mb-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-1">
            <button onClick={() => shift(-1)} aria-label="Previous"
                    className="grid place-items-center w-8 h-8 rounded-lg border border-line-strong text-text-muted hover:text-primary hover:border-primary/50 hover:bg-surface-3 transition-all duration-150 active:scale-95">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => shift(1)} aria-label="Next"
                    className="grid place-items-center w-8 h-8 rounded-lg border border-line-strong text-text-muted hover:text-primary hover:border-primary/50 hover:bg-surface-3 transition-all duration-150 active:scale-95">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <h1 className="page-title !text-[22px] truncate">{title}</h1>
          <button onClick={() => setAnchor(new Date())} className="btn-ghost !py-1 !px-2.5 text-xs">
            Today
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 p-1 rounded-lg border border-line bg-surface-2/50">
            {[['day', CalendarDays], ['week', CalendarRange]].map(([v, Icon]) => (
              <button key={v} onClick={() => setView(v)} title={`${v} view`}
                      className={`grid place-items-center w-7 h-7 rounded-md transition-all duration-150 ${
                        view === v ? 'bg-primary/18 text-primary' : 'text-text-dim hover:text-text-primary'
                      }`}>
                <Icon className="w-4 h-4" />
              </button>
            ))}
          </div>
          <button className="btn-primary inline-flex items-center gap-2" onClick={() => setModal(true)}>
            <Plus className="w-4 h-4" /> Add class
          </button>
        </div>
      </div>

      {/* legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-text-muted">
        {Object.entries(CATEGORY_COLORS).map(([k, c]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: c }} />
            {CATEGORY_LABELS[k]}
          </span>
        ))}
      </div>

      {/* calendar */}
      <div className="table-container !overflow-hidden">
        {/* header row */}
        <div className="flex border-b border-line bg-surface-3/50 sticky top-0 z-20">
          <div className="shrink-0 border-r border-line" style={{ width: GUTTER }} />
          {days.map((d, i) => {
            const isToday = isSameDay(d, now)
            return (
              <div key={i}
                   className={`flex-1 min-w-0 border-l border-line px-2 py-2 text-center ${
                     isToday ? 'bg-primary/10' : ''}`}>
                <div className={`text-[10px] uppercase tracking-wider font-semibold ${
                  isToday ? 'text-primary' : 'text-text-dim'}`}>
                  {format(d, 'EEE')}
                </div>
                <div className={`text-[15px] font-bold leading-tight ${
                  isToday ? 'text-primary' : 'text-text-primary'}`}>
                  {format(d, 'd')}
                </div>
              </div>
            )
          })}
        </div>

        {/* scrolling body */}
        <div ref={bodyRef} className="relative overflow-y-auto" style={{ maxHeight: '62vh' }}>
          <div className="flex" style={{ height: TOTAL_H }}>
            {/* time gutter */}
            <div className="shrink-0 border-r border-line relative bg-surface-2/40" style={{ width: GUTTER }}>
              {hours.map((h, i) => (
                <div key={h} className="absolute right-2 text-[10.5px] text-text-dim tabular-nums -translate-y-1/2"
                     style={{ top: i * PX_PER_HOUR }}>
                  {i === 0 ? '' : format(new Date(2000, 0, 1, h), 'h a')}
                </div>
              ))}
            </div>

            {/* day columns */}
            {days.map((d, di) => {
              const isToday = isSameDay(d, now)
              return (
                <div key={di}
                     className={`flex-1 min-w-0 relative border-l border-line ${isToday ? 'bg-primary/[0.04]' : ''}`}>
                  {/* grid lines */}
                  {hours.map((h, i) => (
                    <div key={h}>
                      <div className="absolute left-0 right-0 border-t pointer-events-none"
                           style={{ top: i * PX_PER_HOUR, borderColor: 'rgb(var(--border))' }} />
                      <div className="absolute left-0 right-0 border-t pointer-events-none"
                           style={{ top: i * PX_PER_HOUR + PX_PER_HOUR / 2,
                                    borderColor: 'rgb(var(--border) / 0.45)' }} />
                    </div>
                  ))}

                  {/* now line */}
                  {isToday && nowVisible && (
                    <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: nowTop }}>
                      <div className="h-px" style={{ background: 'rgb(var(--danger))' }} />
                      <div className="absolute -left-1 -top-[3px] w-[7px] h-[7px] rounded-full"
                           style={{ background: 'rgb(var(--danger))' }} />
                    </div>
                  )}

                  {/* events */}
                  {isLoading ? (
                    <div className="absolute inset-x-1 top-2 space-y-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="skeleton" style={{ height: 46 }} />
                      ))}
                    </div>
                  ) : (
                    byDay[di].map((ev) => (
                      <EventCard key={ev.raw.id} ev={ev} onClick={setDetail} />
                    ))
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {!isLoading && list.length === 0 && (
        <div className="card text-center py-10">
          <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-text-muted">No classes scheduled this {view}.</p>
          <button className="btn-primary mt-4 inline-flex items-center gap-2" onClick={() => setModal(true)}>
            <Plus className="w-4 h-4" /> Add the first class
          </button>
        </div>
      )}

      <ClassModal open={modal} onClose={() => setModal(false)} initialDate={anchor} />

      <ClassDrawer cls={detail} onClose={() => setDetail(null)} colorFor={colorFor} />

    </div>
  )
}
